import { useQuery } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { EXTENDED_GRAPHQL_TIMEOUT_MS } from '@core/services/constants';
import { useMemo } from 'react';

import type { GetAuditSessionsResponse } from '../domain/graphql-types';
import { GET_AUDIT_SESSIONS } from '../graphql/queries/getAuditSessions';

export interface PaginationOptions {
  page?: number;
  pageSize?: number;
  status?: string;
}

/**
 * Hook for fetching audit sessions and community data with pagination support
 * Simplified to eliminate unnecessary domain mapping layers
 */
export function useGetAuditSessions(communityId: string | undefined, paginationOptions: PaginationOptions = {}) {
  const { page = 1, pageSize = 100, status } = paginationOptions;

  const { data, loading, error, refetch } = useQuery<GetAuditSessionsResponse>(GET_AUDIT_SESSIONS, {
    variables: {
      communityId,
      page,
      pageSize,
      status,
    },
    skip: !communityId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network', // Instant cache + fresh network data
    nextFetchPolicy: 'cache-first', // Subsequent queries use cache-first
    notifyOnNetworkStatusChange: true, // Show loading during refetch
    context: { timeout: EXTENDED_GRAPHQL_TIMEOUT_MS },
  });

  // Use GraphQL response directly - no unnecessary mapping layers
  const result = useMemo(() => {
    if (!data?.community) {
      return { sessions: [], community: null };
    }

    // Use GraphQL data directly - no artificial transformations
    return {
      sessions: data.community.auditSessions || [],
      community: data.community,
    };
  }, [data]);

  // Transform error using simplified adapter pattern
  const domainError = useMemo(() => {
    if (!error) return undefined;

    // Map GraphQL error to user-friendly format
    const mappedError = transformError(error);

    // Return error with onRetry function for compatibility with existing UI
    return {
      ...mappedError,
      onRetry: () => refetch(),
    };
  }, [error, refetch]);

  // Create a refetch function that accepts pagination options
  const refetchWithPagination = (newPaginationOptions?: PaginationOptions) => {
    const updatedOptions = { ...paginationOptions, ...newPaginationOptions };

    return refetch({
      communityId,
      page: updatedOptions.page || 1,
      pageSize: updatedOptions.pageSize || 100,
      status: updatedOptions.status,
    });
  };

  return {
    sessions: result.sessions,
    community: result.community,
    loading,
    error: domainError,
    refetch: refetchWithPagination,
    currentPage: page,
    pageSize,
  };
}
